import { isUpdaterFn, UpdaterFn } from '../types'

const isSSR = typeof window === 'undefined'

const storageArea = !isSSR ? window.localStorage : null

export type Parser<TValue> = (data: string | null) => TValue | null
export type Serializer<TValue> = (data: TValue | null) => string

export type Subscriber<TValue> = (value: TValue | null) => void

export interface LocalStorageOptions<TValue> {
  parser?: Parser<TValue>
  serializer?: Serializer<TValue>

  defaultValue?: TValue | null

  syncTabs?: boolean
  throwOnError?: boolean

  onUpdate?: (data: TValue | null) => void
  onError?: (error: Error) => void
}

const defaultParser: Parser<unknown> = (data) => {
  if (!data) return null

  return JSON.parse(data)
}

const noop = () => {}

/**
 * При изменении LocalStorage, браузер отправит event во все ДРУГИЕ вкладки
 *
 * В текущую вкладку событие не отправляется.
 * Чтобы обеспечить синхронизацию значений внутри одной страницы, самостоятельно создаем event
 */
class SamePageStorageEvent extends StorageEvent {}

const dispatchStorageEvent = (key: string, newValue: string | null, oldValue: string | null) => {
  window.dispatchEvent(
    new SamePageStorageEvent('storage', { key, oldValue, storageArea, newValue }),
  )
}

type StorageEventListener = (event: StorageEvent) => void

abstract class StorageEventProxy {
  private static subscribers = new Set<StorageEventListener>()

  private static DOMEventListener = (event: StorageEvent) => {
    this.subscribers.forEach((callback) => callback(event))
  }

  public static addListener = (callback: StorageEventListener) => {
    if (this.subscribers.size === 0) {
      window.addEventListener('storage', this.DOMEventListener)
    }

    this.subscribers.add(callback)
  }

  public static removeListener = (callback: StorageEventListener) => {
    this.subscribers.delete(callback)

    if (this.subscribers.size === 0) {
      window.removeEventListener('storage', this.DOMEventListener)
    }
  }
}

export class LocalStorage<TValue = string> {
  private subscribers = new Set<Subscriber<TValue>>()

  private options!: Required<LocalStorageOptions<TValue>>

  constructor(
    private readonly key: string,
    options: LocalStorageOptions<TValue> = {},
  ) {
    this.patchOptions(options)

    if (isSSR) return

    const { defaultValue, onUpdate, serializer, throwOnError, onError } = this.options

    try {
      const hasItem = !!localStorage.getItem(key)

      if (!hasItem && defaultValue !== null) {
        const serialized = serializer(defaultValue)

        localStorage.setItem(key, serialized)
        dispatchStorageEvent(key, serialized, null)
        onUpdate(defaultValue)
      }
    } catch (error) {
      onError(error as Error)

      if (throwOnError) throw error
    }
  }

  public patchOptions = (options: Partial<LocalStorageOptions<TValue>>) => {
    this.options = {
      parser: defaultParser as Parser<TValue>,
      serializer: JSON.stringify as Serializer<TValue>,

      defaultValue: null,

      syncTabs: false,
      throwOnError: false,

      onError: noop,
      onUpdate: noop,

      ...options,
    }
  }

  public getCurrentValue = () => {
    if (isSSR) return this.options.defaultValue

    try {
      const stored = localStorage.getItem(this.key)
      const parsed = this.options.parser(stored)

      return parsed
    } catch (error) {
      this.options.onError(error as Error)

      if (this.options.throwOnError) {
        throw error
      }

      return null
    }
  }

  private notifyChanged = (value: TValue | null) => {
    this.subscribers.forEach((callback) => callback(value))

    this.options.onUpdate(value)
  }

  public setValue = (value: TValue | UpdaterFn<TValue> | null) => {
    if (value === null) {
      this.removeValue()
      return
    }

    try {
      const serializedOldValue = localStorage.getItem(this.key)

      const newValue = isUpdaterFn(value) ? value(this.options.parser(serializedOldValue)) : value

      const serializedNewValue = this.options.serializer(newValue)

      localStorage.setItem(this.key, serializedNewValue)
      dispatchStorageEvent(this.key, serializedNewValue, serializedOldValue)

      return true
    } catch (error) {
      this.options.onError(error as Error)

      if (this.options.throwOnError) throw error

      return false
    }
  }

  public removeValue = () => {
    const oldValue = localStorage.getItem(this.key)

    localStorage.removeItem(this.key)

    dispatchStorageEvent(this.key, null, oldValue)
  }

  private handleStorageChange = (event: StorageEvent) => {
    const { parser, onError, throwOnError, syncTabs } = this.options

    if (event.key !== this.key || event.storageArea !== storageArea) return

    const isFromDifferentWindow = !(event instanceof SamePageStorageEvent)

    if (isFromDifferentWindow && syncTabs === false) {
      return
    }

    try {
      const parsed = parser(event.newValue)

      this.notifyChanged(parsed)
    } catch (error: unknown) {
      onError(error as Error)

      if (throwOnError) throw error
    }
  }

  public subscribe = (callback: Subscriber<TValue>) => {
    this.subscribers.add(callback)

    StorageEventProxy.addListener(this.handleStorageChange)

    return () => {
      this.removeListener(callback)
    }
  }

  public removeListener = (callback: Subscriber<TValue>) => {
    this.subscribers.delete(callback)

    if (this.subscribers.size === 0) {
      StorageEventProxy.removeListener(this.handleStorageChange)
    }
  }

  public clearAllListeners = () => {
    this.subscribers.clear()

    StorageEventProxy.removeListener(this.handleStorageChange)
  }
}
